import { PackagePlus, Search, Trash2 } from "lucide-react";
import { eur, ALIQUOTE, NATURE } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COLS = "md:grid-cols-[2rem_8rem_minmax(0,1fr)_5rem_7rem_4.5rem_7rem_10rem_4.5rem]";
const IVA_OPTS = [...ALIQUOTE.filter((a) => a).map((a) => [String(a), `${a}% - IVA ${a}%`]), ...Object.entries(NATURE).map(([k, v]) => [`0|${k}`, `0% - ${v}`])];
const ivaVal = (ln) => (Number(ln.aliquota) ? String(Number(ln.aliquota)) : `0|${ln.natura || "N2.2"}`);
const ivaOpts = (ln) => (Number(ln.aliquota) && !ALIQUOTE.includes(Number(ln.aliquota)) ? [[String(Number(ln.aliquota)), `${Number(ln.aliquota)}% - IVA ${Number(ln.aliquota)}%`], ...IVA_OPTS] : IVA_OPTS);
const lineTot = (ln) => Number(ln.quantita || 0) * Number(ln.prezzo || 0) * (1 - Number(ln.sconto || 0) / 100);
const fromProduct = (ln, p) => ({ ...ln, codice: p.codice || "", descrizione: p.descrizione, prezzo: p.prezzo, aliquota: p.aliquota, natura: p.natura || "", unita: p.unita || "" });
const findCode = (products, code) => code && products.find((x) => (x.codice || "").toLowerCase() === code.trim().toLowerCase());

const Cell = ({ label, children, className = "" }) => (
  <label className={`min-w-0 ${className}`}><span className="text-[10px] uppercase text-slate-500 md:hidden">{label}</span>{children}</label>
);

const LineItem = ({ ln, i, products, onChange, onRemove, onNewProduct }) => {
  const set = (k, v) => onChange(i, { ...ln, [k]: v });
  const byCode = (code) => {
    const p = findCode(products, code);
    if (p) onChange(i, fromProduct(ln, p));
  };
  const setIva = (v) => {
    const [a, n] = v.split("|");
    onChange(i, { ...ln, aliquota: Number(a), natura: n || "" });
  };
  return (
    <div data-testid={`line-row-${i}`} className={`grid grid-cols-6 items-end gap-2 border-b border-slate-100 px-2 py-2.5 md:items-center ${COLS}`}>
      <span className="col-span-6 text-xs font-semibold text-slate-500 md:col-span-1 md:text-center">{i + 1}</span>
      <Cell label="Codice" className="col-span-2 md:col-span-1">
        <Input data-testid={`line-code-${i}`} className="h-9 font-mono text-xs" value={ln.codice || ""}
          onFocus={(e) => { e.currentTarget.dataset.start = e.currentTarget.value; }} onChange={(e) => set("codice", e.target.value)}
          onBlur={(e) => e.target.value !== e.target.dataset.start && byCode(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { byCode(e.currentTarget.value); e.currentTarget.dataset.start = e.currentTarget.value; } }} />
      </Cell>
      <Cell label="Descrizione" className="col-span-4 md:col-span-1">
        <Input data-testid={`line-desc-${i}`} className="h-9" value={ln.descrizione} onChange={(e) => set("descrizione", e.target.value)} />
      </Cell>
      <Cell label="Quantità" className="col-span-2 md:col-span-1">
        <Input data-testid={`line-qty-${i}`} className="h-9 text-right font-mono" type="number" step="0.01" value={ln.quantita} onChange={(e) => set("quantita", e.target.value)} />
      </Cell>
      <Cell label="Prezzo €" className="col-span-2 md:col-span-1">
        <Input data-testid={`line-price-${i}`} className="h-9 text-right font-mono" type="number" step="0.01" value={ln.prezzo} onChange={(e) => set("prezzo", e.target.value)} />
      </Cell>
      <Cell label="Sc. %" className="col-span-2 md:col-span-1">
        <Input data-testid={`line-discount-${i}`} className="h-9 px-2 text-right font-mono" type="number" step="0.01" value={ln.sconto} onChange={(e) => set("sconto", e.target.value)} />
      </Cell>
      <Cell label="Importo" className="col-span-2 md:col-span-1">
        <div data-testid={`line-total-${i}`} className="flex h-9 items-center justify-end font-mono text-sm">{eur(lineTot(ln))}</div>
      </Cell>
      <Cell label="Codice IVA" className="col-span-4 md:col-span-1">
        <Select value={ivaVal(ln)} onValueChange={setIva}>
          <SelectTrigger data-testid={`line-vat-${i}`} className="h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{ivaOpts(ln).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
      </Cell>
      <div className="col-span-6 flex justify-end md:col-span-1">
        {onNewProduct && !findCode(products, ln.codice) && (
          <Button type="button" variant="ghost" size="icon" title="Salva come nuovo articolo" data-testid={`line-new-product-${i}`} onClick={() => onNewProduct(i, ln)}><PackagePlus className="h-4 w-4" /></Button>
        )}
        <Button type="button" variant="ghost" size="icon" title="Elimina riga" data-testid={`line-remove-${i}`} onClick={() => onRemove(i)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

const QuickAdd = ({ products, onPick, n }) => (
  <div className={`grid grid-cols-6 items-center gap-2 bg-slate-50/70 px-2 py-2.5 ${COLS}`}>
    <span className="hidden text-center text-xs text-slate-400 md:block">{n}</span>
    <div className="col-span-6 md:col-span-2">
      <Select value="" onValueChange={(pid) => onPick(products.find((p) => p.id === pid))} disabled={!products.length}>
        <SelectTrigger data-testid="line-quick-product" className="h-9 bg-white text-xs text-slate-500">
          <span className="flex items-center gap-2"><Search className="h-3.5 w-3.5" />{products.length ? "Ricerca rapida prodotti" : "Nessun articolo in catalogo"}</span>
        </SelectTrigger>
        <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.codice ? `${p.codice} · ` : ""}{p.descrizione} · {eur(p.prezzo)}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  </div>
);

export const InvoiceLines = ({ lines, products, setLine, onRemove, onNewProduct, onAdd }) => (
  <div data-testid="invoice-lines-table" className="overflow-hidden rounded-lg border border-slate-200">
    <div className={`hidden gap-2 bg-slate-100 px-2 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600 md:grid ${COLS}`}>
      <span className="text-center">Nr.</span><span>Codice</span><span>Descrizione</span><span className="text-right">Quantità</span><span className="text-right">Prezzo</span>
      <span className="text-right">Sc. %</span><span className="text-right">Importo</span><span>Codice IVA</span><span className="text-right">Info</span>
    </div>
    {lines.map((ln, i) => <LineItem key={i} ln={ln} i={i} products={products} onChange={setLine} onRemove={onRemove} onNewProduct={onNewProduct} />)}
    <QuickAdd products={products} n={lines.length + 1} onPick={(p) => p && onAdd(p)} />
    <div className="border-t border-slate-100 px-3 py-2 text-right text-xs text-slate-500" data-testid="lines-count">{lines.length} {lines.length === 1 ? "riga" : "righe"} totali</div>
  </div>
);

export { fromProduct as productLine };
